import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, UserRole } from '../lib/types/api';
import { apiClient } from '../lib/api/client';
import { useCurrentUser, useLogin, useLogout } from '../lib/hooks/useApi';
import { useQueryClient } from '@tanstack/react-query';
import { useLoading } from './LoadingContext';
import { extractErrorMessage, isAuthError } from '../lib/utils/errorHandling';
import { tokenMonitor } from '../lib/services/TokenMonitorService';
import SessionWarningModal from '../components/auth/SessionWarningModal';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isProfileComplete: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;
  hasPermission: (permission: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProfileComplete, setIsProfileComplete] = useState(false);
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [sessionTimeLeft, setSessionTimeLeft] = useState(0);
  const queryClient = useQueryClient();
  const { showLoading, hideLoading } = useLoading();

  const { data: currentUser, isLoading: isLoadingUser, error: currentUserError } = useCurrentUser();

  const loginMutation = useLogin({
    onSuccess: async (data) => {
      try {
        showLoading('Authenticating...', 'ai');
        // Store tokens
        apiClient.setAuthToken(data.access_token);
        localStorage.setItem('refresh_token', data.refresh_token);
        console.log('✅ Tokens stored successfully in AuthContext');
        
        // Force a re-fetch of user data by invalidating the query
        // This will trigger useCurrentUser to fetch the user data
        console.log('🔄 AuthContext: Forcing user data refetch after login');
        queryClient.invalidateQueries({ queryKey: ['currentUser'] });
        
        // User data will be fetched by the useCurrentUser hook
        // The hook should automatically detect the new token and fetch user data
      } catch (error) {
        console.error('❌ Failed to store tokens in AuthContext:', error);
        hideLoading();
      }
    },
    onError: (error) => {
      console.error('Login failed:', error);
      hideLoading();
      // Re-throw the error with a user-friendly message
      const friendlyError = new Error(extractErrorMessage(error));
      friendlyError.name = error.name || 'LoginError';
      throw friendlyError;
    },
  });

  const logoutMutation = useLogout({
    onSuccess: () => {
      console.log('✅ Logout mutation succeeded');
    },
    onError: (error) => {
      console.error('❌ Logout mutation failed:', error);
    },
  });

  useEffect(() => {
    console.log('🔄 AuthContext: currentUser changed:', currentUser);
    if (currentUser) {
      console.log('👤 AuthContext: Setting user state:', currentUser);
      setUser(currentUser);
      
      // Check if profile is complete
      // Profile is complete if phone is provided and password is set
      const profileComplete = !!(
        (currentUser as any).phone && ((currentUser as any).password_set === true)
      );
      setIsProfileComplete(profileComplete);
      console.log('🔄 AuthContext: Profile complete:', profileComplete);
      
      hideLoading(); // Hide loading when user data is loaded
    } else {
      console.log('❌ AuthContext: currentUser is null/undefined');
      setIsProfileComplete(false);
    }
  }, [currentUser, hideLoading]);

  // Handle authentication errors
  useEffect(() => {
    if (currentUserError) {
      console.log('❌ AuthContext: currentUser error:', currentUserError);
      // Avoid clearing tokens if a refresh is in progress; let interceptor finish
      const refreshing = (apiClient as any).isRefreshInProgress?.() === true;
      if (isAuthError(currentUserError) && !refreshing) {
        console.log('❌ AuthContext: Auth error detected and no refresh in progress, clearing tokens');
        apiClient.clearAuthToken();
        setUser(null);
        setIsLoading(false);
      } else if (refreshing) {
        console.log('🔄 AuthContext: 401 during refresh; deferring token clear');
      }
    }
  }, [currentUserError]);

  // Setup token monitoring when user is authenticated
  useEffect(() => {
    if (user && apiClient.isAuthenticated()) {
      console.log('🚀 AuthContext: Starting token monitoring for authenticated user');
      
      // Configure token monitor
      tokenMonitor.updateConfig({
        refreshBufferMinutes: 5, // Refresh 5 minutes before expiration
        checkIntervalSeconds: 30, // Check every 30 seconds
        onTokenRefresh: () => {
          console.log('✅ AuthContext: Token refreshed successfully');
          setShowSessionWarning(false);
        },
        onTokenExpired: () => {
          console.log('❌ AuthContext: Token expired, logging out');
          logoutMutation.mutate();
        },
        onRefreshFailed: (error) => {
          console.error('❌ AuthContext: Token refresh failed:', error);
          setShowSessionWarning(true);
          setSessionTimeLeft(0);
        }
      });

      // Start monitoring
      tokenMonitor.startMonitoring();

      // Check for session warning
      const checkSessionWarning = () => {
        const status = tokenMonitor.getTokenStatus();
        if (status.willExpireSoon && status.timeUntilExpiration > 0) {
          setShowSessionWarning(true);
          setSessionTimeLeft(status.timeUntilExpiration);
        }
      };

      // Check immediately and then every 30 seconds
      checkSessionWarning();
      const warningInterval = setInterval(checkSessionWarning, 30000);

      return () => {
        console.log('🛑 AuthContext: Stopping token monitoring');
        tokenMonitor.stopMonitoring();
        clearInterval(warningInterval);
      };
    } else {
      // Stop monitoring if user is not authenticated
      tokenMonitor.stopMonitoring();
      setShowSessionWarning(false);
    }
  }, [user]);

  useEffect(() => {
    // Check if user is already authenticated on app load
    const checkInitialAuth = () => {
      const hasToken = apiClient.isAuthenticated();
      console.log('🔄 AuthContext: Initial auth check, hasToken:', hasToken);
      
      if (hasToken) {
        // User has token, let the useCurrentUser hook handle the rest
        // Don't set isLoading to false yet, wait for useCurrentUser to complete
        console.log('🔄 AuthContext: Token found, waiting for user data...');
        // Keep loading state true until user data is fetched
      } else {
        // No token, user is not authenticated
        console.log('🔄 AuthContext: No token found, user not authenticated');
        setIsLoading(false);
      }
    };
    
    checkInitialAuth();
  }, []);

  // Set loading to false when user data is loaded or when there's an error
  useEffect(() => {
    if (currentUser && !isLoadingUser) {
      console.log('✅ AuthContext: User data loaded, setting loading to false');
      setIsLoading(false);
    } else if (!isLoadingUser && !currentUser && apiClient.isAuthenticated()) {
      // We have a token but no user data and not loading - this might mean the token is invalid
      // But let's be more conservative and only clear if we get a 401 error
      console.log('❌ AuthContext: Token present but no user data, keeping token for now');
      setIsLoading(false);
    } else if (!isLoadingUser && !currentUser && !apiClient.isAuthenticated()) {
      // No token and no user data and not loading - user is not authenticated
      console.log('❌ AuthContext: No token and no user data, user not authenticated');
      setIsLoading(false);
    }
  }, [currentUser, isLoadingUser]);

  // Load and apply UI preferences on login
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (!apiClient.isAuthenticated()) return;
        const prefs = await apiClient.getUiPreferences();
        if (!mounted) return;
        const menuBase = (prefs as any)?.menu?.base;
        if (menuBase) {
          const hex = menuBase.replace('#','');
          const n = hex.length === 3 ? hex.split('').map((ch: string) => ch + ch).join('') : hex;
          const r = parseInt(n.slice(0,2), 16);
          const g = parseInt(n.slice(2,4), 16);
          const b = parseInt(n.slice(4,6), 16);
          const rgba = (a: number) => `rgba(${r}, ${g}, ${b}, ${a})`;
          const root = document.documentElement as any;
          root.style.setProperty('--menu-bg-start', rgba(0.28));
          root.style.setProperty('--menu-bg-end', rgba(0.12));
          root.style.setProperty('--menu-border', rgba(0.30));
          root.style.setProperty('--menu-active-start', rgba(0.36));
          root.style.setProperty('--menu-active-end', rgba(0.18));
          root.style.setProperty('--menu-active-border', rgba(0.55));
        }
      } catch {}
    })();
    return () => { mounted = false; };
  }, [currentUser]);

  const login = async (email: string, password: string): Promise<void> => {
    await loginMutation.mutateAsync({ email, password });
  };

  const logout = async (): Promise<void> => {
    try {
      console.log('🔄 AuthContext: Starting logout process');
      showLoading('Logging out...', 'ai');
      
      await logoutMutation.mutateAsync();
      console.log('✅ AuthContext: Logout mutation completed successfully');
      
      // Manually clear user state to ensure immediate UI update
      setUser(null);
      console.log('✅ AuthContext: User state cleared');
      
      // Add a small delay to ensure loading spinner is visible
      setTimeout(() => {
        hideLoading();
        // Force redirect to login page
        console.log('🔄 AuthContext: Redirecting to login page');
        window.location.href = '/login';
      }, 500);
      
    } catch (error) {
      console.error('❌ AuthContext: Logout failed:', error);
      hideLoading();
      // Even if the mutation fails, clear the user state locally
      setUser(null);
      // Still redirect to login page
      window.location.href = '/login';
      throw error;
    }
  };

  const hasRole = (role: UserRole): boolean => {
    if (!user) return false;
    return user.role === role;
  };

  const hasAnyRole = (roles: UserRole[]): boolean => {
    if (!user) return false;
    return roles.includes(user.role);
  };

  const hasPermission = (permission: string): boolean => {
    if (!user) return false;
    
    // Use dynamic scopes from the user object if available
    if (user.scopes && Array.isArray(user.scopes)) {
      // Check if user has the specific permission
      if (user.scopes.includes(permission)) {
        return true;
      }
      
      // Check if user has all:admin scope (grants all permissions)
      if (user.scopes.includes('all:admin')) {
        return true;
      }
      
      // Check for legacy permission mappings
      const legacyMappings: Record<string, string[]> = {
        'candidates:read': ['candidates:view'],
        'candidates:write': ['candidates:add', 'candidates:edit', 'candidates:delete'],
        'requirements:read': ['requirements:view'],
        'requirements:write': ['requirements:add', 'requirements:edit', 'requirements:delete'],
        'submissions:read': ['candidates:view'],
        'submissions:write': ['candidates:add', 'candidates:edit', 'candidates:delete'],
        'queries:read': ['candidates:view'],
        'queries:write': ['candidates:add', 'candidates:edit', 'candidates:delete'],
        'team:manage': ['user_management:access'],
        'operations:manage': ['clients:add', 'clients:edit', 'clients:delete'],
        'compliance:manage': ['audit_params:view'],
        'admin:access': ['all:admin'],
        'system:manage': ['all:admin'],
        'user:manage': ['user_management:access'],
        'recruiter:access': ['candidates:view', 'requirements:view']
      };
      
      const mappedScopes = legacyMappings[permission] || [];
      return mappedScopes.some(scope => user.scopes!.includes(scope));
    }
    
    // Fallback to hardcoded role permissions if scopes not available
    const rolePermissions: Record<UserRole, string[]> = {
      recruiter: ['recruiter:access', 'candidates:read', 'candidates:write', 'requirements:read', 'submissions:read', 'submissions:write'],
      team_lead: [
        'recruiter:access',
        'candidates:read', 'candidates:write',
        'requirements:read', 'requirements:write',
        'submissions:read', 'submissions:write',
        'queries:read', 'queries:write', 'queries:approve',
        'team:manage'
      ],
      ops_head: [
        'recruiter:access',
        'candidates:read', 'candidates:write',
        'requirements:read', 'requirements:write',
        'submissions:read', 'submissions:write',
        'queries:read', 'queries:write', 'queries:approve',
        'team:manage',
        'operations:manage',
        'compliance:manage'
      ],
      ceo: [
        'recruiter:access',
        'candidates:read', 'candidates:write',
        'requirements:read', 'requirements:write',
        'submissions:read', 'submissions:write',
        'queries:read', 'queries:write', 'queries:approve',
        'team:manage',
        'operations:manage',
        'compliance:manage',
        'admin:access',
        'system:manage'
      ],
      admin: [
        'recruiter:access',
        'candidates:read', 'candidates:write',
        'requirements:read', 'requirements:write',
        'submissions:read', 'submissions:write',
        'queries:read', 'queries:write', 'queries:approve',
        'team:manage',
        'operations:manage',
        'compliance:manage',
        'admin:access',
        'system:manage',
        'user:manage'
      ],
      manager: [
        'recruiter:access',
        'candidates:read', 'candidates:write',
        'requirements:read', 'requirements:write',
        'submissions:read', 'submissions:write',
        'queries:read', 'queries:write',
        'team:manage'
      ],
      viewer: [
        'candidates:read',
        'requirements:read',
        'submissions:read',
        'queries:read'
      ],
      mis_coordinator: [
        'recruiter:access',
        'candidates:read',
        'requirements:read',
        'submissions:read'
      ],
      hr_coordinator: [
        'recruiter:access',
        'candidates:read',
        'requirements:read',
        'submissions:read'
      ]
    };

    const userPermissions = rolePermissions[user.role] || [];
    return userPermissions.includes(permission);
  };

  // Determine loading state
  const isActuallyLoading = isLoading || (apiClient.isAuthenticated() && isLoadingUser);
  
  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading: isActuallyLoading,
    isProfileComplete,
    login,
    logout,
    hasRole,
    hasAnyRole,
    hasPermission,
  };

  // Debug logging
  console.log('🔄 AuthContext: Rendering with user:', user, 'isAuthenticated:', !!user, 'isLoading:', isLoading || isLoadingUser);
  console.log('🔍 AuthContext: Detailed state:', {
    user,
    hasUser: !!user,
    isLoading,
    isLoadingUser,
    combinedLoading: isLoading || isLoadingUser,
    isAuthenticated: !!user
  });

  return (
    <AuthContext.Provider value={value}>
      {children}
      <SessionWarningModal
        isOpen={showSessionWarning}
        onClose={() => setShowSessionWarning(false)}
        onRefresh={() => {
          setShowSessionWarning(false);
          // Token will be refreshed by the monitor
        }}
        onLogout={() => {
          setShowSessionWarning(false);
          logoutMutation.mutate();
        }}
        timeLeft={sessionTimeLeft}
      />
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
